import * as React from 'react';
import { PLAN_OZELLIKLERI, PLANLAR, slugOner, type Plan, type TesisKurSonuc } from '@hotelcms/shared';
import { Button } from '@/components/ui/button';
import { Kart, KartBaslik, KartGovde, Rozet } from '@/components/ui/card';
import { Alan, Secim } from '@/components/ui/field';
import { ApiHatasi } from '@/lib/api';
import { adminApi } from '@/lib/admin-api';

interface OdaTipiSatir {
  kod: string;
  ad: string;
  adet: number;
  maksYetiskin: number;
  gecelikFiyat: number;
}

const VARSAYILAN_TIPLER: OdaTipiSatir[] = [
  { kod: 'STD', ad: 'Standart', adet: 10, maksYetiskin: 2, gecelikFiyat: 3200 },
];

/**
 * "Ala Otel Akyaka urunu aldi" ekrani.
 * Form doldurulur, tek istekte tesis + yonetici hesabi + odalar + fiyat
 * olusur ve <slug>.otelcms.com aninda acilir.
 */
export function YeniTesis({ platformDomain, onKapat }: { platformDomain: string; onKapat: () => void }) {
  const [ad, setAd] = React.useState('');
  const [slug, setSlug] = React.useState('');
  const [slugElle, setSlugElle] = React.useState(false);
  const [slugDurum, setSlugDurum] = React.useState<{ musait: boolean; sebep: string | null } | null>(null);

  const [tip, setTip] = React.useState('BUTIK');
  const [plan, setPlan] = React.useState<Plan>('DENEME');
  const [eposta, setEposta] = React.useState('');
  const [telefon, setTelefon] = React.useState('');
  const [adres, setAdres] = React.useState('');
  const [vergiNo, setVergiNo] = React.useState('');

  const [yoneticiAd, setYoneticiAd] = React.useState('');
  const [yoneticiEposta, setYoneticiEposta] = React.useState('');

  const [kbsSaglayici, setKbsSaglayici] = React.useState('KAPALI');
  const [kbsTesisKodu, setKbsTesisKodu] = React.useState('');
  const [kbsKullanici, setKbsKullanici] = React.useState('');
  const [kbsSifre, setKbsSifre] = React.useState('');

  const [odaTipleri, setOdaTipleri] = React.useState<OdaTipiSatir[]>(VARSAYILAN_TIPLER);
  const [gonderiliyor, setGonderiliyor] = React.useState(false);
  const [hata, setHata] = React.useState<string | null>(null);
  const [sonuc, setSonuc] = React.useState<TesisKurSonuc | null>(null);

  // Tesis adindan adres onerisi (kullanici elle degistirmediyse)
  React.useEffect(() => {
    if (!slugElle) setSlug(slugOner(ad));
  }, [ad, slugElle]);

  // Adres musaitligi canli kontrol
  React.useEffect(() => {
    if (slug.length < 3) {
      setSlugDurum(null);
      return;
    }
    const zamanlayici = setTimeout(() => {
      adminApi<{ musait: boolean; sebep: string | null }>(`/slug-musait?slug=${encodeURIComponent(slug)}`)
        .then(setSlugDurum)
        .catch(() => setSlugDurum(null));
    }, 350);
    return () => clearTimeout(zamanlayici);
  }, [slug]);

  const toplamOda = odaTipleri.reduce((t, o) => t + (Number(o.adet) || 0), 0);
  const limit = PLAN_OZELLIKLERI[plan].odaLimiti;
  const limitAsildi = toplamOda > limit;

  function tipGuncelle(i: number, alan: keyof OdaTipiSatir, deger: string | number) {
    setOdaTipleri((onceki) => onceki.map((o, n) => (n === i ? { ...o, [alan]: deger } : o)));
  }

  async function gonder(e: React.FormEvent) {
    e.preventDefault();
    setGonderiliyor(true);
    setHata(null);
    try {
      const cevap = await adminApi<TesisKurSonuc>('/tesisler', {
        method: 'POST',
        body: JSON.stringify({
          ad,
          slug,
          tip,
          plan,
          eposta,
          telefon: telefon || null,
          adres: adres || null,
          vergiNo: vergiNo || null,
          yoneticiAd,
          yoneticiEposta,
          kbsSaglayici,
          kbsTesisKodu: kbsTesisKodu || null,
          kbsKullanici: kbsKullanici || null,
          kbsSifre: kbsSifre || null,
          odaTipleri: odaTipleri
            .filter((o) => o.kod && o.ad && Number(o.adet) > 0)
            .map((o) => ({
              kod: o.kod.toUpperCase(),
              ad: o.ad,
              adet: Number(o.adet),
              maksYetiskin: Number(o.maksYetiskin),
              gecelikFiyat: Number(o.gecelikFiyat),
            })),
          notlar: null,
        }),
      });
      setSonuc(cevap);
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : 'Tesis olusturulamadi.');
    } finally {
      setGonderiliyor(false);
    }
  }

  if (sonuc) return <KurulumTamam sonuc={sonuc} onKapat={onKapat} />;

  return (
    <form onSubmit={gonder} className="mx-auto flex w-full max-w-4xl flex-col gap-5 p-4 sm:p-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Yeni tesis</h1>
          <p className="text-slate-600">Tesis, yönetici hesabı ve odalar tek adımda oluşturulur.</p>
        </div>
        <Button type="button" varyant="sessiz" onClick={onKapat}>
          Vazgeç
        </Button>
      </header>

      {hata && <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-red-800">{hata}</div>}

      <Kart>
        <KartBaslik>
          <h2 className="text-lg font-semibold">Tesis bilgileri</h2>
        </KartBaslik>
        <KartGovde className="grid gap-4 sm:grid-cols-2">
          <Alan etiket="Tesis adı" value={ad} onChange={(e) => setAd(e.target.value)} required autoFocus placeholder="Ala Otel Akyaka" />
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-slate-700">İnternet adresi</label>
            <div className="flex items-center gap-1">
              <input
                value={slug}
                onChange={(e) => {
                  setSlugElle(true);
                  setSlug(e.target.value.toLowerCase());
                }}
                className={
                  'h-12 min-w-0 flex-1 rounded-lg border px-3 text-base focus:outline-none focus:ring-2 ' +
                  (slugDurum && !slugDurum.musait
                    ? 'border-red-400 bg-red-50 ring-red-100'
                    : slugDurum?.musait
                      ? 'border-emerald-400 bg-emerald-50 ring-emerald-100'
                      : 'border-slate-300 ring-marka-100')
                }
                required
              />
              <span className="shrink-0 text-sm text-slate-500">.{platformDomain}</span>
            </div>
            {slugDurum && (
              <p className={slugDurum.musait ? 'text-sm text-emerald-700' : 'text-sm text-red-600'}>
                {slugDurum.musait ? 'Bu adres uygun' : slugDurum.sebep}
              </p>
            )}
          </div>

          <Secim
            etiket="Tesis tipi"
            value={tip}
            onChange={(e) => setTip(e.target.value)}
            secenekler={[
              { deger: 'BUTIK', metin: 'Butik otel' },
              { deger: 'OTEL', metin: 'Otel' },
              { deger: 'APART', metin: 'Apart' },
              { deger: 'PANSIYON', metin: 'Pansiyon' },
            ]}
          />
          <Secim
            etiket="Plan"
            value={plan}
            onChange={(e) => setPlan(e.target.value as Plan)}
            secenekler={PLANLAR.map((p) => ({
              deger: p,
              metin: `${p} — ${PLAN_OZELLIKLERI[p].odaLimiti} oda, ${PLAN_OZELLIKLERI[p].aciklama}`,
            }))}
          />

          <Alan etiket="Tesis e-postası" type="email" value={eposta} onChange={(e) => setEposta(e.target.value)} required />
          <Alan etiket="Telefon" value={telefon} onChange={(e) => setTelefon(e.target.value)} />
          <Alan etiket="Adres" value={adres} onChange={(e) => setAdres(e.target.value)} />
          <Alan etiket="Vergi no" value={vergiNo} onChange={(e) => setVergiNo(e.target.value)} />
        </KartGovde>
      </Kart>

      <Kart>
        <KartBaslik>
          <h2 className="text-lg font-semibold">Tesis yöneticisi</h2>
          <Rozet renk="bilgi">Geçici parola üretilecek</Rozet>
        </KartBaslik>
        <KartGovde className="grid gap-4 sm:grid-cols-2">
          <Alan etiket="Yetkili adı" value={yoneticiAd} onChange={(e) => setYoneticiAd(e.target.value)} required />
          <Alan
            etiket="Yetkili e-postası"
            type="email"
            value={yoneticiEposta}
            onChange={(e) => setYoneticiEposta(e.target.value)}
            ipucu="Bu hesap OWNER yetkisiyle açılır"
            required
          />
        </KartGovde>
      </Kart>

      <Kart>
        <KartBaslik>
          <h2 className="text-lg font-semibold">Odalar</h2>
          <Rozet renk={limitAsildi ? 'hata' : 'notr'}>
            {toplamOda} / {limit} oda
          </Rozet>
        </KartBaslik>
        <KartGovde className="flex flex-col gap-3">
          {odaTipleri.map((o, i) => (
            <div key={i} className="grid gap-3 sm:grid-cols-[6rem_1fr_6rem_6rem_8rem_auto] sm:items-end">
              <Alan etiket="Kod" value={o.kod} onChange={(e) => tipGuncelle(i, 'kod', e.target.value.toUpperCase())} />
              <Alan etiket="Oda tipi adı" value={o.ad} onChange={(e) => tipGuncelle(i, 'ad', e.target.value)} />
              <Alan etiket="Adet" type="number" min={0} value={o.adet} onChange={(e) => tipGuncelle(i, 'adet', Number(e.target.value))} />
              <Alan etiket="Kişi" type="number" min={1} value={o.maksYetiskin} onChange={(e) => tipGuncelle(i, 'maksYetiskin', Number(e.target.value))} />
              <Alan etiket="Gecelik ₺" type="number" min={0} value={o.gecelikFiyat} onChange={(e) => tipGuncelle(i, 'gecelikFiyat', Number(e.target.value))} />
              <Button
                type="button"
                varyant="sessiz"
                onClick={() => setOdaTipleri((p) => p.filter((_, n) => n !== i))}
                disabled={odaTipleri.length === 1}
              >
                Sil
              </Button>
            </div>
          ))}
          <div>
            <Button
              type="button"
              varyant="ikincil"
              onClick={() => setOdaTipleri((p) => [...p, { kod: '', ad: '', adet: 0, maksYetiskin: 2, gecelikFiyat: 0 }])}
            >
              + Oda tipi ekle
            </Button>
          </div>
          <p className="text-sm text-slate-500">
            Gecelik fiyat girilirse 120 günlük fiyat takvimi otomatik açılır. Boş bırakırsanız tesis kendi doldurur.
          </p>
        </KartGovde>
      </Kart>

      <Kart>
        <KartBaslik>
          <h2 className="text-lg font-semibold">KBS bilgileri</h2>
          <Rozet>İsteğe bağlı</Rozet>
        </KartBaslik>
        <KartGovde className="grid gap-4 sm:grid-cols-2">
          <Secim
            etiket="KBS sağlayıcı"
            value={kbsSaglayici}
            onChange={(e) => setKbsSaglayici(e.target.value)}
            secenekler={[
              { deger: 'KAPALI', metin: 'Kapalı (sonra tanımlanacak)' },
              { deger: 'MANUEL_FORM', metin: 'Manuel form (CSV indir)' },
              { deger: 'RESMI_WS', metin: 'Resmi web servisi' },
            ]}
          />
          <Alan
            etiket="Tesis kodu (TssKod)"
            value={kbsTesisKodu}
            inputMode="numeric"
            maxLength={6}
            onChange={(e) => setKbsTesisKodu(e.target.value.replace(/\D/g, ''))}
            ipucu="JGNK'nın tesise verdiği kod — bağlı karakol komutanlığından öğrenilir"
          />
          <Alan
            etiket="Yetkili T.C. no (KullaniciTC)"
            value={kbsKullanici}
            inputMode="numeric"
            maxLength={11}
            autoComplete="off"
            onChange={(e) => setKbsKullanici(e.target.value.replace(/\D/g, ''))}
            ipucu="Web servisi çağıracak yetkilinin kimlik numarası"
          />
          <Alan
            etiket="Web servis şifresi"
            type="password"
            value={kbsSifre}
            onChange={(e) => setKbsSifre(e.target.value)}
            autoComplete="new-password"
            ipucu="Kişiye özel KBS web servis şifresi. Şifrelenerek saklanır, bir daha görüntülenemez."
          />
          <div className="sm:col-span-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <strong>Resmi web servisi sabit IP ister.</strong> KBS, çağrıyı yapan IP adresinin kendi
            arayüzünde kayıtlı olmasını şart koşar. Tesis bunu sağlayamıyorsa
            <strong> Manuel form</strong> seçeneğini kullanın — akış (kuyruk, tekrar deneme, süre takibi) aynıdır.
          </div>
        </KartGovde>
      </Kart>

      <div className="flex items-center justify-end gap-3">
        {limitAsildi && <span className="text-sm text-red-600">Oda sayısı {plan} planının limitini aşıyor.</span>}
        <Button type="submit" boyut="xl" varyant="basari" yukleniyor={gonderiliyor} disabled={limitAsildi || slugDurum?.musait === false}>
          Tesisi oluştur
        </Button>
      </div>
    </form>
  );
}

function KurulumTamam({ sonuc, onKapat }: { sonuc: TesisKurSonuc; onKapat: () => void }) {
  const [kopyalandi, setKopyalandi] = React.useState<string | null>(null);
  const kopyala = (metin: string, ad: string) => {
    void navigator.clipboard.writeText(metin).then(() => {
      setKopyalandi(ad);
      setTimeout(() => setKopyalandi(null), 1500);
    });
  };

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-5 p-4 sm:p-6">
      <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-6 text-center">
        <p className="text-4xl">✅</p>
        <h1 className="mt-2 text-2xl font-bold text-emerald-900">Tesis hazır</h1>
        <p className="text-emerald-800">
          {sonuc.olusanOda} oda, {sonuc.olusanOdaTipi} oda tipi ve yönetici hesabı oluşturuldu.
        </p>
      </div>

      {sonuc.alanAdi.durum !== 'ACILDI' && (
        <div
          className={
            'rounded-xl border px-4 py-3 text-sm ' +
            (sonuc.alanAdi.durum === 'HATA'
              ? 'border-red-300 bg-red-50 text-red-800'
              : 'border-amber-300 bg-amber-50 text-amber-900')
          }
        >
          <p className="font-semibold">Adres henüz açılmamış olabilir</p>
          <p>
            {sonuc.alanAdi.mesaj} Tesis oluşturuldu; adres açılınca panel çalışmaya başlar.
          </p>
        </div>
      )}

      <Kart>
        <KartGovde className="flex flex-col gap-4">
          <Satir etiket="Panel adresi" deger={sonuc.url} onKopyala={kopyala} kopyalandi={kopyalandi} />
          <Satir etiket="Yönetici e-postası" deger={sonuc.yoneticiEposta} onKopyala={kopyala} kopyalandi={kopyalandi} />
          <Satir etiket="Geçici parola" deger={sonuc.geciciParola} onKopyala={kopyala} kopyalandi={kopyalandi} vurgu />
          <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Geçici parola <strong>yalnızca şimdi</strong> görünür; hiçbir yerde saklanmaz. Tesise iletin ve ilk
            girişte değiştirmelerini söyleyin. Kaybolursa panelden yeni parola üretebilirsiniz.
          </div>
        </KartGovde>
      </Kart>

      <div className="flex justify-between">
        <Button varyant="sessiz" onClick={onKapat}>
          Tesis listesine dön
        </Button>
        <a className="inline-flex" href={sonuc.url} target="_blank" rel="noreferrer">
          <Button type="button">Paneli aç →</Button>
        </a>
      </div>
    </div>
  );
}

function Satir({
  etiket,
  deger,
  onKopyala,
  kopyalandi,
  vurgu,
}: {
  etiket: string;
  deger: string;
  onKopyala: (m: string, a: string) => void;
  kopyalandi: string | null;
  vurgu?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3 last:border-0">
      <div className="min-w-0">
        <p className="text-sm text-slate-500">{etiket}</p>
        <p className={'truncate font-mono ' + (vurgu ? 'text-xl font-bold text-slate-900' : 'text-base text-slate-800')}>
          {deger}
        </p>
      </div>
      <Button type="button" varyant="ikincil" boyut="sm" onClick={() => onKopyala(deger, etiket)}>
        {kopyalandi === etiket ? 'Kopyalandı' : 'Kopyala'}
      </Button>
    </div>
  );
}
